// src/pages/Products.jsx
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import "../components/Products.css";

// Build a URL to /public assets (no PUBLIC_URL needed)
const fromPublic = (name) => `/${encodeURIComponent(name)}`;

export default function Products() {
  const [openIdx, setOpenIdx] = useState(null);
  const lastFocusRef = useRef(null);

  // AOS refresh
  useEffect(() => {
    if (window.AOS) window.AOS.refresh();
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    const body = document.body;
    if (openIdx !== null) {
      const prev = body.style.overflow;
      body.style.overflow = "hidden";
      return () => (body.style.overflow = prev);
    }
  }, [openIdx]);

  const products = [
    {
      img: fromPublic("WhatsApp Image 2025-09-29 at 19.20.24_5a4e3881.jpg"),
      title: "Variable Speed Drives (VSD)",
      desc:
        "High-efficiency drives from Nidec & Schneider Electric to precisely control motor speed and torque.",
      link: "/catalogue/vsd",
    },
    {
      img: "https://t3.ftcdn.net/jpg/04/92/17/24/360_F_492172447_amfxpGZkh3qU7gtT1SLobfdOn6eSMnNQ.jpg",
      title: "Contactors",
      desc:
        "Durable, high-performance contactors for reliable switching in industrial and commercial applications.",
      link: "/catalogue/contactors",
    },
    {
      img: "https://www.rockwellautomation.com/content/dam/rockwell-automation/sites/images/products/allenbradley/gl/medlrgprod/1762-micrologix1200family-front1-large-312w255h.jpg",
      title: "PLC Solutions",
      desc:
        "Programmable Logic Controllers and expansion modules for robust automation and control.",
      link: "/catalogue/plc",
    },
    {
      img: "https://tse4.mm.bing.net/th/id/OIP.aip_r2qTWjBhqxeJNnuKqwHaE8?r=0&w=316&h=316&c=7",
      title: "Instrumentation Equipment",
      desc:
        "Sensors, transmitters and panel instruments for precise measurement and monitoring.",
      link: "/catalogue/instrumentation",
    },
  ];

  const openModal = (idx, e) => {
    if (e) {
      // remember the button that opened it
      lastFocusRef.current = e.currentTarget;
    }
    setOpenIdx(idx);
    // small delay so dialog exists before focusing
    setTimeout(() => {
      const el = document.getElementById("product-dialog");
      el?.focus();
    }, 0);
  };

  const closeModal = () => {
    setOpenIdx(null);
    // restore focus
    lastFocusRef.current?.focus?.();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") closeModal();
  };

  const active = openIdx !== null ? products[openIdx] : null;

  return (
    <>
      {/* Scoped styles (ONLY modal + modal buttons) */}
      <style>{`
        /* Modal base */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.5);
          display: grid;
          place-items: center;
          z-index: 1000;
        }
        .modal {
          width: min(720px, 92vw);
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0,0,0,.25);
          overflow: hidden;
          outline: none; /* we'll manage focus programmatically */
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid #eee;
        }
        .modal-title {
          font-size: 1.2rem;
          font-weight: 700;
          margin: 0;
        }
        .modal-close {
          border: none;
          background: transparent;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
        }
        .modal-body {
          display: grid;
          gap: 1rem;
          padding: 1rem 1.25rem 1.25rem;
        }
        @media (min-width: 720px) {
          .modal-body {
            grid-template-columns: 1fr 1.2fr;
            align-items: start;
          }
        }
        .modal-img {
          width: 100%;
          max-height: 320px;
          object-fit: contain;
          background: #f9f9f9;
          border-radius: 8px;
        }
        .modal-desc {
          font-size: 1rem;
          line-height: 1.5;
        }
        .modal-actions {
          display: flex;
          gap: .5rem;
          margin-top: .75rem;
          flex-wrap: wrap;
        }
        .btn-primary-sm {
          border: none;
          background: var(--primary-color);
          color: #fff;
          padding: .5rem .75rem;
          border-radius: 999px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          font-weight: 600;
        }
      `}</style>

      {/* ─── breadcrumb ─── */}
      <section className="page-header-breadcrumb" data-aos="fade-up">
        <h1 className="section-title">Our Products</h1>
        <p className="page-breadcrumb">
          <Link to="/">Home</Link> / <span>Products</span>
        </p>
      </section>

      {/* ─── hero ─── */}
      <section
        className="hero-section"
        data-aos="fade-up"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,.6),rgba(0,0,0,.6)), url('https://picsum.photos/seed/automation-panel/1400/600')",
        }}
      >
        <div className="hero-content">
          <h1>Quality Automation &amp; Electrical Components</h1>
          <p>
            Variable-speed drives, contactors, PLCs and instrumentation — supplied
            with the expertise of Mosike Electrical Contractors.
          </p>
          <div className="hero-buttons">
            <a href="#product-grid" className="btn">
              View All Products
            </a>
            <Link to="/contact" className="btn btn-outline">
              Request a Quote
            </Link>
          </div>
        </div>
      </section>

      {/* ─── simple grid using your new CSS ─── */}
      <section
        id="product-grid"
        className="products-simple"
        data-aos="fade-up"
      >
        <div className="products-simple__intro">
          <h1>Core Product Lines</h1>
          <p>
            Drives, controls and measurement devices trusted by industry leaders.
          </p>
        </div>

        <div className="products-simple__grid">
          {products.map((p, i) => (
            <div
              key={i}
              className="product-tile"
              data-aos="zoom-in"
              data-aos-delay={100 * (i + 1)}
            >
              <div className="product-tile__image">
                <img src={p.img} alt={p.title} loading="lazy" decoding="async" />
              </div>
              <div className="product-tile__content">
                <h3>{p.title}</h3>
                <p>{p.desc}</p>

                <button
                  type="button"
                  className="btn-learn-more"
                  onClick={(e) => openModal(i, e)}
                >
                  Learn More
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── catalogue CTA ─── */}
      <section className="cta-section" data-aos="fade-up">
        <div className="cta-content">
          <h2>Need Detailed Specs &amp; Pricing?</h2>
          <p>Download our full catalogue or contact us for a customised quote.</p>
          <div className="cta-buttons">
            <a
              href="/catalogue/MEC-Catalogue.pdf"
              className="btn btn-white"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Catalogue
            </a>
            <Link to="/contact" className="btn btn-outline">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* ─── MODAL ─── */}
      {active && (
        <div
          className="modal-overlay"
          onClick={closeModal}
          onKeyDown={onKeyDown}
          aria-hidden="false"
        >
          <div
            id="product-dialog"
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-dialog-title"
            aria-describedby="product-dialog-desc"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()} // keep clicks inside from closing
          >
            <div className="modal-header">
              <h3 id="product-dialog-title" className="modal-title">
                {active.title}
              </h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={closeModal}
              >
                &times;
              </button>
            </div>

            <div className="modal-body">
              <img className="modal-img" src={active.img} alt={active.title} />
              <div>
                <p id="product-dialog-desc" className="modal-desc">
                  {active.desc}
                </p>

                <div className="modal-actions">
                  <Link to="/contact" className="btn-primary-sm" onClick={closeModal}>
                    Request a quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
